import { pipe } from 'fp-ts/function';
import lodash from 'lodash';

import { GALLERY_TYPE, IMAGE_TYPE } from './../ricos-content';
import type { DebugMode, PluginMapping, PluginTypeMapper } from './../ricos-types';
import {
  convertRelObjectToString,
  convertRelStringToObject,
} from './../wix-rich-content-common/lib/linkConverters';

import { applyBIGenerics } from './biUtils';
import pluginsStrategy from './pluginsStrategy';
import type { ContentViewerProps, RicosViewerProps } from './types';

const combineMappers = (mappers: PluginTypeMapper[], ...args: $TSFixMe[]): PluginMapping => {
  if (!mappers || mappers.some(resolver => typeof resolver !== 'function')) {
    console.warn(`${mappers} is expected to be a function array`); // eslint-disable-line no-console
    return {};
  }
  return mappers.reduce<PluginMapping>((map, mapper) => Object.assign(map, mapper(...args)), {});
};

export function mergeViewerProps({
  _rcProps,
  isMobile,
  addAnchors,
  onError,
  mediaSettings = {},
  linkSettings = {},
  textAlignment,
  experiments,
  iframeSandboxDomain,
  theme,
  plugins = [],
  content,
  viewerProps,
  onNodeClick,
  debugMode,
  jsdomMode,
  baseUrl,
}: RicosViewerProps & { viewerProps: Partial<ContentViewerProps>; debugMode?: DebugMode[] }) {
  const rcvPluginProps = pluginsStrategy({
    plugins,
    viewerProps,
  });

  const { pauseMedia, disableRightClick } = mediaSettings;
  const { anchorTarget = '_blank', customAnchorScroll } = linkSettings;
  let { relValue, rel } = linkSettings;

  if (relValue) {
    // eslint-disable-next-line no-console
    console.warn(
      // eslint-disable-next-line max-len
      `relValue is deprecated, Please use rel prop instead.`
    );
    rel = convertRelStringToObject(relValue) || rel;
  }
  relValue = convertRelObjectToString(rel);
  const disableDownload = mediaSettings?.disableDownload || disableRightClick;
  // any of ricos props that should be merged into child
  const ricosPropsToMerge: Partial<ContentViewerProps> = {
    addAnchors,
    isMobile,
    config: {
      uiSettings: { disableDownload },
    },
    content,
    onError,
    helpers: {},
    disabled: pauseMedia,
    anchorTarget,
    relValue,
    customAnchorScroll,
    textAlignment,
    experiments,
    iframeSandboxDomain,
    theme,
    onNodeClick,
    jsdomMode,
    baseUrl,
  };

  const mergedRCProps = pipe(
    lodash.merge(
      { locale: 'en', config: {} },
      rcvPluginProps,
      _rcProps,
      ricosPropsToMerge,
      viewerProps
    ),
    applyBIGenerics(() => content?.metadata?.id)
  ) as ContentViewerProps & { typeMappers: PluginTypeMapper[] | PluginMapping };

  return {
    ...mergedRCProps,
    debugMode,
    typeMappers: combineMappers((mergedRCProps.typeMappers || []) as PluginTypeMapper[]),
  };
}

export function hasFullscreen(config: $TSFixMe) {
  const { [IMAGE_TYPE]: imageConfig, [GALLERY_TYPE]: galleryConfig } = config || {};
  const needsFullscreenProvider = !imageConfig?.onExpand || !galleryConfig?.onExpand;
  const isExpandDisabled =
    (!imageConfig || imageConfig.disableExpand) && (!galleryConfig || galleryConfig?.disableExpand);

  return !isExpandDisabled && needsFullscreenProvider;
}
